import Vue from 'vue';
import Vuex from 'vuex';
import { OdpInfo, Medicine } from './models/odpModels';
import appConfig from './appConfig';
import moment from 'moment';

Vue.use(Vuex);

export interface IState {
    preparationDate: Date;
    takingTime: string;
    medicines: Medicine[];
    medicineImages: MedicineImages;
    isAllNormalMedicineLoadCompleted: boolean;
    isAllEmphasizedMedicineLoadCompleted: boolean;
    fetchResult: FetchResult;
    currentSettings: CurrentSettings;
}

interface MedicineImages {
    normal: MedicineImageMap;
    emphasized: MedicineImageMap;
}

interface MedicineImageMap {
    [key: string]: MedicineImage;
}

interface HTMLImageMap {
    [key: string]: HTMLImageElement;
}

interface MedicineImage {
    path: string;
    sizeRatio: number;
}

interface OdpReadResult {
    odpInfo: OdpInfo;
    fetchResult: FetchResult;
}

interface CurrentSettings {
    isAcceptedTerms: boolean;
    isEmphasized?: boolean;
}

export enum ImageMarkType {
    Normal,
    Emphasized,
}

export enum FetchResult {
    Ready, // Storeが呼ばれるたびに入る初期状態
    Success, // fetchで包情報取得に成功した状態
    Old, // fetchには成功したが1年以上前に調剤された包を読んだ状態
    NotFound, // fetchには成功したが包が見つからなかった状態
    InvalidSerialCode, // fetchには成功したが1Dサービス以外のバーコードを読んでいた状態
    Error, // fetchには成功したがハンドリングできないエラーのステータスが返ってきた状態
}

export interface ILoadImagePayload {
    medicines: Medicine[];
}
export interface IUpdateOdpInfoPayload {
    serialCode: string;
}

interface UpdateMedicineImagePayload {
    imageMarkType: ImageMarkType;
    medicineImageMap: MedicineImageMap;
}

// 日付型をデシリアライズするためのreviver関数
function reviver(key: string, value: any) {
    if (value == null ||
        value.constructor !== String ||
        value.search(/^\d{4}-\d{2}-\d{2}/g) === -1) {
        return value;
    }
    return new Date(value);
}

function getErrorInfo(result: FetchResult) {
    const errorInfo: OdpReadResult = {
        odpInfo: {
            preparationDate: new Date(0),
            takingTime: '',
            medicines: [],
        },
        fetchResult: result,
    };
    return errorInfo;
}

/**
 * 画像のフェッチを行う
 * contextはanyで宣言しているが、実態はActionContext<IState, IState>であること。
 * 型情報がないためanyで対応している。
 */
function fetchImages(context: any, imageMarkType: ImageMarkType, medicines: Medicine[]) {

    // 指定された回数だけ呼ばれたら、callback関数を実行する。
    const fullCalledFunc = (expectedCnt: number, callback: any) => {
        let cnt = 0;
        return () => {
            if (++cnt === + expectedCnt) { callback(); }
        };
    };

    const imageArray: HTMLImageMap = {};
    const expectedCount = medicines.length;

    const toBase64 = (image: HTMLImageElement) => {
        // canvas要素を生成してimg要素を反映する
        const cvs = document.createElement('canvas');
        cvs.width = image.width;
        cvs.height = image.height;
        const ctx = cvs.getContext('2d');
        ctx!.drawImage(image, 0, 0);

        // canvas要素をBase64化する
        return cvs.toDataURL('image/jpeg');
    };

    // 全ての薬の画像が読み込まれた後に一度だけ実行される関数
    const loader = fullCalledFunc(expectedCount, () => {

        // 通常画像、刻印強調画像の判定および、画像サイズの計算処理を行う。
        // 画像サイズは最大の薬の画像をもとに、他の画像を相対比でリサイズする。
        const widthArray: number[] = Object.keys(imageArray).map((v) => imageArray[v].width);
        const maxWidth = Math.max(...widthArray);
        const medicineImages: MedicineImageMap = {};

        // 最大の画像サイズとの相対比に応じた画像サイズを計算
        for (const key of Object.keys(imageArray)) {
            const image = imageArray[key];
            // widthが0の場合、画像取得エラーと判断しNoImage画像を指定する
            medicineImages[key] = image.width === 0
                ? { path: require('./assets/no-image.png'), sizeRatio: 1 }
                : { path: toBase64(image), sizeRatio: image.width / maxWidth };

        }

        context.commit(
            'updateMedicineImage',
            {
                imageMarkType,
                medicineImageMap: medicineImages,
            } as UpdateMedicineImagePayload,
        );
        context.commit('completeFetchMedicineImages', imageMarkType);
    });

    // 刻印強調の取得で失敗したときには、NoImageではなく通常画像を取得する。
    const emphasizedErrorLoader = (medicine: Medicine) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        // オブジェクトは刻印強調のキーに対して入れ直す
        imageArray[medicine.emphasizedImageKey] = image;

        // 画像取得先は刻印強調画像ではなくて通常画像にする。
        const imagePath = `${appConfig.medicineImageApiUrl}?imageKey=${encodeURIComponent(medicine.normalImageKey)}`;

        // 通常画像でも失敗した場合にはNoImageを表示するので、エラー時にも通常のloaderをセットする。
        image.onerror = loader;
        image.onload = loader;

        image.src = imagePath;
    };

    for (const medicine of medicines) {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        const imageKey = imageMarkType === ImageMarkType.Normal
            ? medicine.normalImageKey
            : medicine.emphasizedImageKey;
        imageArray[imageKey] = image;

        const imagePath = `${appConfig.medicineImageApiUrl}?imageKey=${encodeURIComponent(imageKey)}`;

        // 刻印強調画像の取得で失敗した場合には通常画像を取得するImageオブジェクトを再生成させるため、ローダーを分ける。
        image.onerror = imageMarkType === ImageMarkType.Normal ? loader : () => emphasizedErrorLoader(medicine);
        image.onload = loader;

        image.src = imagePath;
    }
}

export default new Vuex.Store({
    state: {
        preparationDate: new Date(0), // 最小値を初期値とする
        takingTime: '',
        medicines: [],
        medicineImages: { normal: {}, emphasized: {} },
        isAllNormalMedicineLoadCompleted: false,
        isAllEmphasizedMedicineLoadCompleted: false,
        fetchResult: FetchResult.Ready,
        currentSettings: { isAcceptedTerms: false, isEmphasized: undefined },
    } as IState,
    getters: {
        getMedicineImage: (state) => (imageMarkType: ImageMarkType, medicine: Medicine) => {
            return imageMarkType === ImageMarkType.Normal
                ? state.medicineImages.normal[medicine.normalImageKey]
                : state.medicineImages.emphasized[medicine.emphasizedImageKey];
        },
    },
    mutations: {
        updateOdpInfo(state: IState, payload: OdpReadResult) {
            state.preparationDate = payload.odpInfo.preparationDate;
            state.takingTime = payload.odpInfo.takingTime;
            state.medicines = payload.odpInfo.medicines;
            state.fetchResult = payload.fetchResult;
        },
        startMedicineLoad(state: IState) {
            state.isAllNormalMedicineLoadCompleted = false;
            state.isAllEmphasizedMedicineLoadCompleted = false;
        },
        updateMedicineImage(state: IState, payload: UpdateMedicineImagePayload) {
            if (payload.imageMarkType === ImageMarkType.Normal) {
                state.medicineImages.normal = payload.medicineImageMap;
            } else {
                state.medicineImages.emphasized = payload.medicineImageMap;
            }
        },
        completeFetchMedicineImages(state: IState, imageMarkType: ImageMarkType) {
            if (imageMarkType === ImageMarkType.Normal) {
                state.isAllNormalMedicineLoadCompleted = true;
            } else {
                state.isAllEmphasizedMedicineLoadCompleted = true;
            }
        },
        acceptTerms(state: IState) {
            state.currentSettings.isAcceptedTerms = true;
        },
        updateCurrentIsEmphasized(state: IState, isEmphasized: boolean) {
            state.currentSettings.isEmphasized = isEmphasized;
        },
        initMedicineImages(state: IState) {
            state.medicineImages.normal = {};
            state.medicineImages.emphasized = {};
        },
    },
    actions: {
        updateOdpInfoAction(context, payload: IUpdateOdpInfoPayload) {
            context.commit('initMedicineImages');
            const url = appConfig.odpInfoApiUrl.replace('{serialCode}', encodeURIComponent(payload.serialCode));
            const promise = fetch(url)
                .then(async (response) => {
                    switch (response.status) {
                        case 200:
                            const text = await response.text();
                            const limitDate = moment().add(-1, 'y').locale('ja');
                            const fetchResult: OdpReadResult = {
                                odpInfo: JSON.parse(text, reviver),
                                fetchResult: FetchResult.Ready,
                            };
                            if (limitDate <= moment(fetchResult.odpInfo.preparationDate)) {
                                fetchResult.fetchResult = FetchResult.Success;
                            } else {
                                fetchResult.fetchResult = FetchResult.Old;
                            }
                            return fetchResult;
                        case 404:
                            const notInfo: OdpReadResult = getErrorInfo(FetchResult.NotFound);
                            return notInfo;
                        case 400:
                            const invalidInfo: OdpReadResult = getErrorInfo(FetchResult.InvalidSerialCode);
                            return invalidInfo;
                        default:
                            // TODO 404以外の2xx系などのエラーは別途対応を行う
                            const errorInfo: OdpReadResult = getErrorInfo(FetchResult.Error);
                            console.error('エラー発生 レスポンス内容:%s  ステータスコード: %d', response.text(), response.status);
                            return errorInfo;
                    }
                })
                .then((data) => {
                    console.debug('リクエストURL: %s 取得データ: %o', url, data);
                    context.commit('updateOdpInfo', data);
                    const loadImagePayload: ILoadImagePayload = { medicines: data.odpInfo.medicines };
                    context.dispatch('loadImageAction', loadImagePayload);
                })
                .catch(() => {
                    const errorInfo: OdpReadResult = getErrorInfo(FetchResult.Error);
                    context.commit('updateOdpInfo', errorInfo);
                });
            return promise;
        },
        loadImageAction(context, payload: ILoadImagePayload) {
            context.commit('startMedicineLoad');

            fetchImages(context, ImageMarkType.Normal, payload.medicines);
            fetchImages(context, ImageMarkType.Emphasized, payload.medicines);

        },
    },
});
